import customConfigResource from '@/api/resources/ui/CustomConfigResource'
import sideBarOptions from './sideBarOptions'
import ExpandedSideBar from '@/components/sideBar/ExpandedSideBar'
import CompactSideBar from '@/components/sideBar/CompactSideBar'
import { COMMON_STATE } from '@/api/resources/entityState/common-state'
import { store } from '@/store/store'
import { findNodeByPropertyValue } from '@/components/sideBar/sideBarUtils'
import { UiComponentUtils } from '@/shared/util/ui-component-utils'
import { StoreUtils } from '@/shared/util/store-utils'
import { ToastUtils } from '@/shared/util/toast-utils'
import i18n from '@/lang/i18n'

class ArchiveSideBarStore {
    constructor() {
        this.sideBars = [ ExpandedSideBar, CompactSideBar ]
        this.loadedCustomOptions = false
        this.loadedConfig = false
        this.completeArray = sideBarOptions.expanded.reduce(
            (prev, next) => (
                next.children ? prev.concat(next.children.reduce(
                    (innerPrev, innerNext) => (
                        innerNext.children ? innerPrev.concat(innerNext.children) : innerPrev.concat(innerNext)),
                    [])) : prev.concat(next)),
            [])
        this.defaultOption = this.completeArray.find(obj => obj.id === 'samplesToBeArchivedCount')
        this.state = {
            componentCode: 'uicomponent.archive.sidepanel',
            options: sideBarOptions,
            sideBar: ExpandedSideBar,
            fallbackOption: this.defaultOption,
            selectedOption: this.defaultOption,
            sideBarName: sideBarOptions.sideBarName,
            gridRouteName: 'archiveGrid',
            customOptions: [],
            config: this.getDefaultConfig()
        }
    }
    getDefaultConfig() {
        return {
            defaultOptionId: null,
            sideBarName: ExpandedSideBar.name
        }
    }
    hasLoaded() {
        return this.loadedCustomOptions && this.loadedConfig
    }
    async expandSideBar() {
        this.state.sideBar = ExpandedSideBar
        await this.saveConfig()
    }
    async contractSideBar() {
        this.state.sideBar = CompactSideBar
        await this.saveConfig()
    }
    selectDefaultOption() {
        const option = this.findOptionById(this.state.config.defaultOptionId)
        if (!option) {
            console.warn(`customOption with id ${this.state.config.defaultOptionId} does not exist, selecting fallback option`)
            this.selectFallbackOption()
            return
        }
        this.setSelectedOption(option)
    }
    selectFallbackOption() {
        this.setSelectedOption(this.state.fallbackOption)
    }
    findOptionById(optionId) {
        const options = [ ...this.completeArray, ...this.state.customOptions ]
        return findNodeByPropertyValue('id', optionId, options)
    }
    setSelectedOption(option) {
        this.state.selectedOption = option
    }
    setDefaultOption(option) {
        this.state.config.defaultOptionId = option && option.id
    }
    updateSelectedOption() {
        const customOptionSelectedOption = this.state.customOptions.find(customOption => customOption.id === this.state.selectedOption?.id)
        if (customOptionSelectedOption) {
            this.setSelectedOption(customOptionSelectedOption)
        }
    }
    async loadConfig() {
        const customConfig = await customConfigResource.get(this.state.componentCode)
        if (customConfig) {
            this.setConfig({ ...this.getDefaultConfig(), ...customConfig.config })
        } else {
            this.setConfig(this.getDefaultConfig())
        }
        this.loadedConfig = true
    }
    setConfig(config) {
        this.state.config = config
        this.applyConfigSideBar()
    }
    applyConfigSideBar() {
        const sideBar = this.sideBars.find(i => i.name === this.state.config.sideBarName)
        if (!sideBar) {
            throw new Error(`sideBar named ${this.state.config.sideBarName} not found`)
        }
        this.state.sideBar = sideBar
    }
    async saveConfig() {
        const config = {
            ...this.state.config,
            sideBarName: this.getCurrentSideBarName()
        }
        await customConfigResource.save(this.state.componentCode, config)
        this.state.config = config
    }
    getCurrentSideBarName() {
        if (!this.state.sideBar) {
            throw new Error('state.sideBar is not set')
        }
        return this.state.sideBar.name
    }
    matchSideBarOptionByEntityAndSubentity(option, entityType, entitySubtype) {
        return (
            option.task.entityType === entityType &&
            (!entitySubtype || entitySubtype === option.filterParams?.entitySubtype)
        )
    }
    findSideBarOptionByEntityAndOperations(entity, entityType, entitySubtype, operations) {
        if (this.state.selectedOption &&
            this.matchSideBarOptionByEntityAndSubentity(this.state.selectedOption, entityType, entitySubtype) &&
            (operations.some(operation => this.state.selectedOption.task.operations.includes(operation)) ||
                (this.state.selectedOption.task.areElementsArchived &&
                    !this.state.selectedOption.task.areElementsPendingUnarchiving &&
                    entity.status.code === COMMON_STATE.ARCHIVED))
        ) {
            return this.state.selectedOption
        }
        const foundOption = this.completeArray.find(option => {
            return (
                this.matchSideBarOptionByEntityAndSubentity(option, entityType, entitySubtype) &&
                operations.some(operation => option.task.operations.includes(operation))
            )
        })
        if (foundOption) {
            return foundOption
        }
        return this.completeArray.find(option => {
            return (
                this.matchSideBarOptionByEntityAndSubentity(option, entityType, entitySubtype) &&
                !option.task.areElementsPendingUnarchiving && option.task.areElementsArchived && entity.status.code === COMMON_STATE.ARCHIVED
            )
        })
    }
    /// CUSTOM FILTERS
    async loadCustomOptions() {
        const response = await UiComponentUtils.getCustomFilter(this.state.componentCode, store.state.labId)
        if (response !== null) {
            this.state.customOptions = response.map(customOption => ({
                ...customOption,
                ...customOption.filterCriteria,
                label: customOption.filterName
            }))
            this.loadedCustomOptions = true
        }
    }
    async createCustomOption(customFilterRequest) {
        if (UiComponentUtils.validateCustomFilter(customFilterRequest)) {
            try {
                StoreUtils.updateLoading(true)

                const response =
                    await UiComponentUtils.createCustomFilter(
                        this.state.componentCode,
                        customFilterRequest,
                        store.state.labId)
                if (response !== null) {
                    await this.loadCustomOptions()
                    return true
                }
            } finally {
                StoreUtils.updateLoading(false)
            }
        }
        return false
    }
    async removeCustomOption(customFilterId) {
        try {
            StoreUtils.updateLoading(true)

            const response =
                await UiComponentUtils.removeCustomFilter(
                    this.state.componentCode,
                    customFilterId,
                    store.state.labId)
            if (response !== null) {
                await this.loadCustomOptions()

                if (customFilterId === this.state.selectedOption.id) {
                    this.setSelectedOption(this.defaultOption)
                }
                return true
            }
        } finally {
            StoreUtils.updateLoading(false)
        }
        return false
    }
    async updateCustomOption(customFilter) {
        if (UiComponentUtils.validateCustomFilter(customFilter)) {
            try {
                StoreUtils.updateLoading(true)

                const response =
                    await UiComponentUtils.updateCustomFilter(
                        customFilter.componentCode,
                        customFilter,
                        customFilter.id,
                        customFilter.laboratoryId)
                if (response !== null) {
                    await this.loadCustomOptions()
                    return true
                }
            } finally {
                StoreUtils.updateLoading(false)
            }
        }
        return false
    }
    async renameCustomOption(customFilter) {
        const filter = {
            ...customFilter,
            filterName: customFilter.label
        }
        if (await this.updateCustomOption(filter)) {
            await this.updateSelectedOption()
            ToastUtils.showToastSuccess(i18n.tc('global.renamedFilter'))
        }
    }
}

export default new ArchiveSideBarStore()
